import { readRefreshToken } from "./auth.js";
import { loadGlobalConfig } from "./config.js";
import { ApiError } from "./errors.js";
import { fetchWithTimeout } from "./http.js";
export { ApiError } from "./errors.js";
export class GrimoireClient {
    baseUrl;
    machineToken;
    cachedIdToken;
    refreshInFlight;
    constructor(opts = {}) {
        this.baseUrl = (opts.baseUrl ?? loadGlobalConfig().apiBaseUrl).replace(/\/+$/, "");
        this.machineToken = opts.machineToken ?? process.env.GRIMOIRE_AUTH_TOKEN;
    }
    async bearer() {
        if (this.machineToken)
            return this.machineToken;
        if (this.cachedIdToken && this.cachedIdToken.expiresAt > Date.now() + 60_000) {
            return this.cachedIdToken.token;
        }
        this.refreshInFlight ??= this.refreshIdToken().finally(() => {
            this.refreshInFlight = undefined;
        });
        return this.refreshInFlight;
    }
    // Exchange the Firebase refresh token for a fresh ID token (silent refresh).
    async refreshIdToken() {
        const refresh = readRefreshToken();
        if (!refresh)
            throw new ApiError(401, "not_logged_in", "Run `grimoire login`");
        const res = await fetchWithTimeout(`${this.baseUrl}/auth/cli/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: refresh }),
        });
        if (!res.ok)
            throw new ApiError(res.status, "refresh_failed", "Run `grimoire login`");
        let payload;
        try {
            payload = await res.json();
        }
        catch {
            throw new ApiError(res.status, "refresh_failed", "Malformed refresh response");
        }
        const { id_token, expires_in } = payload;
        if (typeof id_token !== "string" || typeof expires_in !== "number") {
            throw new ApiError(res.status, "refresh_failed", "Malformed refresh response");
        }
        this.cachedIdToken = { token: id_token, expiresAt: Date.now() + expires_in * 1000 };
        return id_token;
    }
    async refreshSession() {
        await this.bearer();
    }
    async send(path, init, auth) {
        const headers = {
            "Content-Type": "application/json",
            ...init.headers,
        };
        if (auth)
            headers.Authorization = `Bearer ${await this.bearer()}`;
        return fetchWithTimeout(`${this.baseUrl}${path}`, { ...init, headers });
    }
    async request(path, init = {}, auth = true) {
        let res = await this.send(path, init, auth);
        // A 401 despite a locally-unexpired ID token means it was revoked server-side:
        // force one refresh and retry exactly once.
        if (res.status === 401 && auth && !this.machineToken) {
            this.cachedIdToken = undefined;
            res = await this.send(path, init, auth);
        }
        return this.parseResponse(res);
    }
    async parseResponse(res) {
        const isJson = res.headers.get("content-type")?.includes("json") ?? false;
        const text = await res.text();
        let body;
        let parsed = false;
        if (isJson && text) {
            try {
                body = JSON.parse(text);
                parsed = true;
            }
            catch {
                parsed = false;
            }
        }
        if (!res.ok) {
            const code = parsed && typeof body === "object" && body !== null
                ? (body.error ?? "error")
                : "error";
            throw new ApiError(res.status, code, parsed ? body : text);
        }
        if (isJson)
            return (parsed ? body : {});
        return text;
    }
    search(input) {
        return this.request("/v1/search", {
            method: "POST",
            body: JSON.stringify(input),
        });
    }
    listSources(q) {
        return this.request(`/v1/sources${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    }
    listVersions(sourceId) {
        return this.request(`/v1/sources/${encodeURIComponent(sourceId)}/versions`);
    }
    getContext(chunkId, window = 2) {
        return this.request(`/v1/documents/${encodeURIComponent(chunkId)}/context?window=${window}`);
    }
    reportResult(chunkId, verdict, note) {
        return this.request("/v1/feedback", {
            method: "POST",
            body: JSON.stringify({ chunk_id: chunkId, verdict, note }),
        });
    }
    submitSource(body) {
        return this.request("/v1/sources", { method: "POST", body: JSON.stringify(body) });
    }
    getJob(jobId) {
        return this.request(`/v1/jobs/${encodeURIComponent(jobId)}`);
    }
}
